import { useCallback, useMemo } from "react"

import { useScopedLocalStorage } from "@/workspaces"

import type { MetadataColumnType } from "../utils/metadataColumnType"

export interface AddedMetadataColumn {
  key: string
  type: MetadataColumnType
}

interface MetadataColumnState {
  overrides: Record<string, MetadataColumnType>
  hidden: string[]
  added: AddedMetadataColumn[]
}

const EMPTY_STATE: MetadataColumnState = {
  overrides: {},
  hidden: [],
  added: [],
}

// The `testSetId || "new"` fallback means an unsaved, brand-new test set
// shares one storage slot until it's saved — useScopedLocalStorage further
// scopes that by org + workspace, or two orgs drafting a new test set in the
// same browser would read and overwrite each other's column overrides.
function storageKeyBase(testSetId: string): string {
  return `testSet.metadataColumns.${testSetId || "new"}`
}

export interface MetadataColumnStateApi {
  overrides: Record<string, MetadataColumnType>
  hiddenKeys: Set<string>
  addedColumns: AddedMetadataColumn[]
  setOverride: (key: string, type: MetadataColumnType) => void
  clearOverride: (key: string) => void
  hideColumn: (key: string) => void
  showColumn: (key: string) => void
  addColumn: (key: string, type: MetadataColumnType) => void
  removeAddedColumn: (key: string) => void
}

export function useMetadataColumnState(
  testSetId: string,
): MetadataColumnStateApi {
  const [state, setState] = useScopedLocalStorage<MetadataColumnState>(
    storageKeyBase(testSetId),
    EMPTY_STATE,
  )

  const hiddenKeys = useMemo(() => new Set(state.hidden ?? []), [state.hidden])

  const setOverride = useCallback(
    (key: string, type: MetadataColumnType) => {
      setState({
        ...state,
        overrides: { ...state.overrides, [key]: type },
      })
    },
    [state, setState],
  )

  const clearOverride = useCallback(
    (key: string) => {
      if (!(key in state.overrides)) {
        return
      }
      const next = { ...state.overrides }
      delete next[key]
      setState({ ...state, overrides: next })
    },
    [state, setState],
  )

  const hideColumn = useCallback(
    (key: string) => {
      if (hiddenKeys.has(key)) {
        return
      }
      setState({ ...state, hidden: [...state.hidden, key] })
    },
    [state, hiddenKeys, setState],
  )

  const showColumn = useCallback(
    (key: string) => {
      if (!hiddenKeys.has(key)) {
        return
      }
      setState({
        ...state,
        hidden: state.hidden.filter((k) => k !== key),
      })
    },
    [state, hiddenKeys, setState],
  )

  const addColumn = useCallback(
    (key: string, type: MetadataColumnType) => {
      const trimmed = key.trim()
      if (!trimmed) {
        return
      }
      const existing = state.added ?? []
      if (existing.some((c) => c.key === trimmed)) {
        return
      }
      setState({
        ...state,
        added: [...existing, { key: trimmed, type }],
      })
    },
    [state, setState],
  )

  const removeAddedColumn = useCallback(
    (key: string) => {
      const existing = state.added ?? []
      if (!existing.some((c) => c.key === key)) {
        return
      }
      setState({
        ...state,
        added: existing.filter((c) => c.key !== key),
      })
    },
    [state, setState],
  )

  return {
    overrides: state.overrides,
    hiddenKeys,
    addedColumns: state.added ?? [],
    setOverride,
    clearOverride,
    hideColumn,
    showColumn,
    addColumn,
    removeAddedColumn,
  }
}
